/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Stack,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Autocomplete,
  Typography,
} from '@mui/material';
import {
  LoadingButton,
} from '@mui/lab';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  useForm,
  getFieldProps,
  getSubmitButtonProps,
  getFormProps,
  TimezoneSelector,
  InputPhone,
  Validate,
} from '@marageti/z4-lib';
import {
  Controller,
} from 'react-hook-form';
import { Phone, TimeZone } from '@marageti/z4-sdk/lib/standards';
import { TravelerTags } from '@marageti/z4-sdk/lib/people';
import { Partner } from '@marageti/z4-sdk/lib/travel';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import PartnerSelector from '../../partner-lookup-input';
import CountrySelector from '../../country-lookup-input';

type TagOption = {
  title: string;
  value: TravelerTags;
};

type TravelerFormDefaultValues = {
  givenName: string | undefined,
  familyName: string | undefined,
  phone: Phone | undefined,
  email: string | undefined,
  timeZone: TimeZone | undefined,
  countryOfResidence: string | undefined,
  tags: TagOption[] | undefined,
  referringPartner: Partner | null,
};

const DEFAULT_VALUES = {
  givenName: '',
  familyName: '',
  phone: {
    prefix: '',
    number: '',
  },
  email: '',
  timeZone: {
    name: '',
    rawOffsetInMinutes: 0,
  } as TimeZone,
  countryOfResidence: '',
  tags: [],
  referringPartner: null,
} as TravelerFormDefaultValues;

const schema = z.object({
  givenName: z.string().min(1, { message: 'Required' }),
  familyName: z.string().min(1, { message: 'Required' }),
  phone: z.object({
    prefix: z.string(),
    number: z.string(),
  }).refine((value) => {
    if (!value.number) return true;
    return Validate.phone(`${value.prefix}${value.number}`);
  }, { message: 'Please enter a valid number.' }),
  email: z.string().min(1, { message: 'Required ' }).email('Invalid email'),
  timeZone: z.object({
    name: z.string(),
    rawOffsetInMinutes: z.any(),
  }),
  countryOfResidence: z.string().min(1, { message: 'Required' }),
  tags: z.array(z.object({
    title: z.string(),
    value: z.string(),
  })),
  referringPartner: z.object({
    id: z.string(),
    name: z.string(),
  }).nullable(),
});

type TravelerFormProps = {
  onSubmit: (data: any) => void,
  submissionError: ApiError | undefined,
  onCancel?: () => void
};

const TravelerForm = ({
  onSubmit,
  submissionError,
  onCancel = undefined,
}: TravelerFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    control,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  return (
    <form {...getFormProps(handleSubmit(onSubmit))}>
      <Stack spacing={3}>
        <Stack useFlexGap>
          <TextField
            label="First Name"
            {...register('givenName')}
            {...getFieldProps('givenName', errors)}
            required
          />
          <TextField
            label="Last Name"
            {...register('familyName')}
            {...getFieldProps('familyName', errors)}
            required
          />
          <TextField
            label="Email Address"
            {...register('email')}
            {...getFieldProps('email', errors)}
            required
          />
          <Controller
            control={control}
            name="countryOfResidence"
            render={({ field }) => (
              <CountrySelector
                required
                id="countryOfResidence"
                label="Country of Residence"
                {...getFieldProps('countryOfResidence', errors)}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="timeZone"
            render={({ field }) => (
              <TimezoneSelector
                id="timeZone-selector"
                label="Timezone"
                {...getFieldProps('timeZone', errors)}
                {...field}
                value={field.value || undefined}
              />
            )}
          />

          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <InputPhone
                id="phone"
                label="Phone Number"
                preferredCountries={['US', 'AU', 'CA', 'SG', 'GB']}
                {...getFieldProps('phone', errors)}
                {...field}
                value={field.value || { prefix: '', number: '' }}
              />
            )}
          />
          <Autocomplete
            multiple
            options={[{
              title: 'VIP',
              value: TravelerTags.VIP,
            },
            {
              title: 'FOBY',
              value: TravelerTags.FOBY,
            },
            {
              title: 'Red Flag',
              value: TravelerTags.RED_FLAG,
            },
            {
              title: 'Bad Apple',
              value: TravelerTags.BAD_APPLE,
            },
            {
              title: 'Travel Agent',
              value: TravelerTags.TRAVEL_AGENT,
            },
            {
              title: 'Junk',
              value: TravelerTags.JUNK,
            },
            ]}
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            ChipProps={{
              color: 'secondary',
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
              />
            )}
            value={watch('tags') || []}
            onChange={(_: any, selectedOptions: TagOption[]) => {
              setValue('tags', selectedOptions, {
                shouldValidate: true,
              });
            }}
          />

          <PartnerSelector
            value={watch('referringPartner') || undefined}
            onChange={(_: any, selectedPartner: Partner | null) => {
              setValue('referringPartner', selectedPartner, {
                shouldValidate: true,
              });
            }}
            inputProps={{
              label: 'PRL - Lifetime',
              placeholder: 'Search for a partner',
              helperText: <Typography variant="caption" color="error">This will make the traveler a lifetime PRL of the selected partner</Typography>,
            }}
          />
        </Stack>

        {submissionError && (
          <Alert severity="error">
            <AlertTitle>There was an error submitting the form.</AlertTitle>
            {`Code: ${submissionError.code}: ${submissionError.message}`}
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={(theme) => ({
            [theme.breakpoints.down('desktop')]: {
              marginTop: 'auto',
              flexDirection: 'column-reverse',
              '& > :not(:first-of-type)': {
                margin: theme.spacing(0, 0, 2.5, 0),
              },
            },
          })}
        >
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Next
          </LoadingButton>

          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
};

export default TravelerForm;
